import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/context/ToastContext';
import { useEchoStore } from '@/context/EchoContext';
import { echoService } from '@/services/echoService';
import { getErrorMessage } from '@/api/api';
import type {
  EchoProfileResponse,
  EchoDraftResponse,
  UpdatePricingRequest,
} from '@/types/echo_creation';

interface UseMyEchoReturn {
  echo: EchoProfileResponse | null;
  drafts: EchoDraftResponse[];
  trainedDraft: EchoDraftResponse | null;
  isLoading: boolean;
  isUpdating: boolean;
  updateVisibility: (isPublic: boolean) => Promise<void>;
  updatePricing: (data: UpdatePricingRequest) => Promise<void>;
  saveEcho: (draftId: string, isPublic: boolean) => Promise<void>;
  retrain: (draftId: string) => void;
  refresh: () => Promise<void>;
}

export const useMyEcho = (): UseMyEchoReturn => {
  const { addToast } = useToast();
  const { myEchoes, myDrafts, isLoading, refreshEchoes } = useEchoStore();
  const [isUpdating, setIsUpdating] = React.useState(false);
  const navigate = useNavigate();

  // Derive the active echo
  const echo = React.useMemo(() => myEchoes[0] ?? null, [myEchoes]);

  // A persisted persona is usable even if an older backend left the draft in
  // training_uploaded after an optional enrichment failure.
  const trainedDraft = React.useMemo(
    () =>
      myDrafts.find(
        (draft) =>
          (draft.status === 'training_completed' || draft.status === 'training_uploaded') &&
          draft.persona_json
      ) ?? null,
    [myDrafts]
  );

  // Initial Load
  React.useEffect(() => {
    refreshEchoes();
  }, [refreshEchoes]);

  // Status Polling: If status is 'training' or 'pending', poll every 10s
  React.useEffect(() => {
    let pollInterval: number | undefined;

    if (echo && (echo.status === 'training' || echo.status === 'pending')) {
      pollInterval = window.setInterval(() => {
        refreshEchoes();
      }, 10000); // 10 seconds
    }

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [echo, refreshEchoes]);

  const updateVisibility = React.useCallback(
    async (isPublic: boolean) => {
      if (!echo) return;
      setIsUpdating(true);
      try {
        await echoService.updateVisibility(echo.id, { is_public: isPublic });
        await refreshEchoes(); // Sync global state
        addToast({
          title: 'Success',
          subtitle: `Echo is now ${isPublic ? 'Public' : 'Private'}`,
          type: 'success',
        });
      } catch (error) {
        addToast({
          title: 'Update Failed',
          subtitle: getErrorMessage(error),
          type: 'error',
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [echo, refreshEchoes, addToast]
  );

  const updatePricing = React.useCallback(
    async (data: UpdatePricingRequest) => {
      if (!echo) return;
      setIsUpdating(true);
      try {
        await echoService.updatePricing(echo.id, data);
        await refreshEchoes(); // Sync global state
        addToast({
          title: 'Success',
          subtitle: 'Pricing configuration saved.',
          type: 'success',
        });
      } catch (error) {
        addToast({
          title: 'Update Failed',
          subtitle: getErrorMessage(error),
          type: 'error',
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [echo, refreshEchoes, addToast]
  );

  const saveEcho = React.useCallback(
    async (draftId: string, isPublic: boolean) => {
      setIsUpdating(true);
      try {
        await echoService.publishTrained(draftId, isPublic);
        await refreshEchoes();
        addToast({
          title: 'Echo Saved!',
          subtitle: `Your echo is now ${isPublic ? 'public' : 'private'}.`,
          type: 'success',
        });
      } catch (error) {
        addToast({
          title: 'Save Failed',
          subtitle: getErrorMessage(error),
          type: 'error',
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [refreshEchoes, addToast]
  );

  const retrain = React.useCallback(
    (draftId: string) => {
      navigate(`/create-echo?draftId=${encodeURIComponent(draftId)}&retrain=true`);
    },
    [navigate]
  );

  return {
    echo,
    drafts: myDrafts,
    trainedDraft,
    isLoading,
    isUpdating,
    updateVisibility,
    updatePricing,
    saveEcho,
    retrain,
    refresh: refreshEchoes,
  };
};

export default useMyEcho;
